import React, { useMemo } from "react";
import { SaleOrder } from "@/types/general";
import dayjs from "dayjs";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { flattenLines } from "@components/Dashboard/pages/Sales/Orders/utils";
import {
    getLocationKey,
    toReservationAllocation
} from "@components/Dashboard/pages/Sales/Orders/components/MutationLayout/parts/ReservationsSection/utils";

interface ViewReservationsSectionProps {
    reservations: SaleOrder.Reservation[];
    lines: SaleOrder.Components.Lines;
    expirationDate: string | null;
}

interface ResolvedReservation {
    product: {
        id: number;
        name: string;
        code: string;
    };
    quantity: number;
    allocations: {
        key: string;
        label: string | null; // null -> global reservation
        quantity: number;
    }[];
}

/**
 * Builds a map of product_id -> product info from the order lines.
 * This allows rebuilding full product data from reservations that only contain product_id.
 */
export function buildProductMapFromLines(lines: SaleOrder.Components.Lines): Map<number, { id: number; name: string; code: string }> {
    const productsMap = new Map<number, { id: number; name: string; code: string }>();

    const processLine = (lineData: SaleOrder.Components.LineData) => {
        const product = lineData.product;
        if (!product || productsMap.has(product.id)) return;

        productsMap.set(product.id, {
            id: product.id,
            name: product.name,
            code: product.code
        });
    };

    lines.forEach(line => {
        if (line.type === "line") {
            processLine(line.data);
        } else if (line.type === "group") {
            line.data.lines.forEach(groupLine => processLine(groupLine.data));
        }
    });

    return productsMap;
}

/**
 * Reservations reference locations only by ids, so the labels are taken from the stock locations of the line products.
 * Keys are `${product_id}>${location key}`.
 */
function buildLocationLabelsFromLines(lines: SaleOrder.Components.Lines): Map<string, string> {
    const labels = new Map<string, string>();

    flattenLines(lines).forEach(({ data }) => {
        data.product.locations.forEach(location => {
            const key = getLocationKey({ store_id: location.location_id, section_id: location.sub_location_id ?? null });

            labels.set(
                `${data.product.id}>${key}`,
                location.section_name ? `${location.location_name} - ${location.section_name}` : location.location_name
            );
        });
    });

    return labels;
}

/**
 * The connector is pinned to the first text line rather than to the middle of the row, so it stays
 * put when a long location wraps onto a second line. Everything it draws derives from these two.
 */
const ROW_PADDING_Y = 6;
const LINE_HEIGHT = 20;
const CONNECTOR_Y = ROW_PADDING_Y + LINE_HEIGHT / 2;
const DOT_SIZE = 7;

function ViewReservationRow({ reservation }: { reservation: ResolvedReservation }) {
    const { t } = useTranslation("", { keyPrefix: "sales.orders.general.reservations" });

    // Purely global reservations look the same as before per-location reservations were introduced
    const hasPerLocationReservations = reservation.allocations.some(allocation => allocation.label !== null);

    return (
        <div className="w-full py-2.5 px-4 text-sm transition-colors hover:bg-white/60">
            <div className="flex items-center">
                {/* Product info */}
                <span className="text-accent underline">
                    <Link to={`/dashboard/products/${reservation.product.id}/details`}>
                        {reservation.product.code}
                    </Link>
                </span>
                <span className="ml-3 text-accent truncate">{reservation.product.name}</span>

                <span className="mx-1 text-gray-500">·</span>

                {/* Left to be picked - next to product info */}
                <div className="flex items-center gap-1">
                    <span className="text-gray-500 whitespace-nowrap">{t("leftToBePicked")}:</span>
                    <span className="text-accent font-medium">{reservation.quantity}</span>
                </div>
            </div>

            {/* Where that quantity waits, branching off the product it belongs to */}
            {hasPerLocationReservations && (
                <div className="mt-1">
                    {reservation.allocations.map((allocation, index) => (
                        <ReservationLocationNode
                            key={allocation.key}
                            label={allocation.label ?? t("anyLocation")}
                            quantity={allocation.quantity}
                            isLast={index === reservation.allocations.length - 1}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}

/**
 * One location of a product's reservation, drawn as a branch of it.
 * Mirrors the merge tree of the Stock count module, so both read the same way.
 */
function ReservationLocationNode({
    label,
    quantity,
    isLast,
}: {
    label: string;
    quantity: number;
    isLast: boolean;
}) {
    const { t } = useTranslation("", { keyPrefix: "sales.orders.general.reservations" });

    return (
        <div className="relative pl-[24px]">
            {/* Trunk: runs to the next sibling, and stops at the elbow on the last node */}
            <div
                className="absolute left-[7px] top-0 w-px bg-[#B3B5EB]"
                style={isLast ? { height: CONNECTOR_Y } : { bottom: 0 }}
            />
            {/* Elbow */}
            <div
                className="absolute left-[7px] h-px w-[14px] bg-[#B3B5EB]"
                style={{ top: CONNECTOR_Y }}
            />
            {/* Node */}
            <div
                className="absolute left-[4px] rounded-full bg-[#7556FA]"
                style={{
                    top: CONNECTOR_Y - DOT_SIZE / 2,
                    height: DOT_SIZE,
                    width: DOT_SIZE,
                }}
            />

            <div
                className="text-gray-500"
                style={{
                    paddingTop: ROW_PADDING_Y,
                    paddingBottom: ROW_PADDING_Y,
                    lineHeight: `${LINE_HEIGHT}px`,
                }}
            >
                <span className="text-accent">{label}</span>
                <span> · </span>
                <span>{t("reservedQuantity")}:</span>{" "}
                <span className="text-accent font-medium">{quantity}</span>
            </div>
        </div>
    );
}

export default function ViewReservationsSection({ reservations, lines, expirationDate }: ViewReservationsSectionProps) {
    const { t } = useTranslation("", { keyPrefix: "sales.orders.general.reservations" });

    // Build product map from lines to resolve product info
    const productMap = useMemo(() => buildProductMapFromLines(lines), [lines]);
    const locationLabels = useMemo(() => buildLocationLabelsFromLines(lines), [lines]);

    // Resolve reservations with full product and location info, grouping per-location records by product
    const resolvedReservations = useMemo<ResolvedReservation[]>(() => {
        const resolved = new Map<number, ResolvedReservation>();

        reservations.forEach(reservation => {
            const product = productMap.get(reservation.product_id);
            if (!product) return;

            const allocation = toReservationAllocation(reservation);
            const key = getLocationKey(allocation.location);

            let label: string | null = null;

            if (allocation.location !== null) {
                const locationLabel = locationLabels.get(`${product.id}>${key}`);

                if (locationLabel === undefined) {
                    throw new Error(
                        `Reservation #${reservation.id} references location "${key}", which is not linked to product #${product.id}`
                    );
                }

                label = locationLabel;
            }

            const entry = resolved.get(product.id) ?? { product, quantity: 0, allocations: [] };
            entry.quantity += allocation.quantity;
            entry.allocations.push({ key, label, quantity: allocation.quantity });
            resolved.set(product.id, entry);
        });

        return Array.from(resolved.values());
    }, [reservations, productMap, locationLabels]);

    // Don't render if no reservations
    if (resolvedReservations.length === 0) return null;

    const formattedExpirationDate = expirationDate
        ? dayjs(expirationDate).format("DD.MM.YYYY")
        : null;

    return (
        <div className="bg-[#F9F9F9] rounded-lg overflow-hidden">
            {/* Header */}
            <div className="p-4 pb-2 flex items-center justify-between gap-4">
                <span className="text-lg font-medium text-accent">
                    {t("heading")}
                </span>
                {formattedExpirationDate && (
                    <div className="text-gray-500">
                        <span className="mr-2">{t("expirationDate")}:</span>
                        <span className="text-accent">{formattedExpirationDate}</span>
                    </div>
                )}
            </div>

            {/* Rows */}
            <div className="pb-2">
                {resolvedReservations.map(reservation => (
                    <ViewReservationRow
                        key={reservation.product.id}
                        reservation={reservation}
                    />
                ))}
            </div>
        </div>
    );
}
